// @story HIR-HR21
// InterviewBriefPanel — an AI interview stage's brief, written and approved in
// the stage's own panel ("Configure assessment"). Design "Interview briefs &
// guardrails", 30 Oct 2026, §A: schools never write or see a prompt; they
// say what the interview is FOR.
//
//   - the brief: a goal, the questions (each saying which rubric line it
//     gathers evidence for), the rubric (a weight, and what strong, adequate
//     and weak sound like), and the length, language and tone;
//   - "Draft with AI" (the Assessment Designer), "Copy from another role",
//     "Save draft" and "Send for approval";
//   - the Brief Checker's problems are shown next to the field they are about,
//     in plain words; a problem that must be fixed stops "Send for approval";
//   - the approver (not the person who wrote or sent it) sees Approve and
//     Send back.
//
// GET  /interview-briefs/stage/:jobId/:stageId     what this panel shows
// GET  /interview-briefs/options                    the dropdowns
// PUT  /interview-briefs/stage/:jobId/:stageId/draft            Save draft
// POST /interview-briefs/stage/:jobId/:stageId/draft-with-ai    Draft with AI
// POST /interview-briefs/stage/:jobId/:stageId/copy             Copy from another role
// GET  /interview-briefs/library?excludeJobId=      approved briefs to copy
// GET  /interview-briefs/:briefId                    canApprove for the waiting one
// POST /interview-briefs/:briefId/submit | approve | send-back
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { Button, Card, Input, StatusPill } from '../../ds';
import SomethingWrong from './SomethingWrong';

const DEFAULT_OPTIONS = {
  durations: [10, 15, 20, 25, 30, 40, 45],
  languages: [{ value: 'en', label: 'English' }, { value: 'hi', label: 'Hindi' }, { value: 'mr', label: 'Marathi' }],
  tones: [{ value: 'warm', label: 'Warm and encouraging' }, { value: 'friendly', label: 'Friendly and professional' }, { value: 'formal', label: 'Formal' }],
};
const EMPTY = {
  goal: '',
  questions: [{ text: '', criterionId: 'C1' }],
  rubric: [{ id: 'C1', name: '', weight: 100, levels: { 5: '', 3: '', 1: '' } }],
  durationMinutes: 20,
  language: 'en',
  tone: 'friendly',
};
const day = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const contentOf = (b) => (b ? {
  goal: b.goal || '',
  questions: (b.questions || []).map((q) => ({ text: q.text, criterionId: q.criterionId || '' })),
  rubric: (b.rubric || []).map((c) => ({ id: c.id, name: c.name, weight: c.weight, levels: { 5: c.levels?.[5] || '', 3: c.levels?.[3] || '', 1: c.levels?.[1] || '' } })),
  durationMinutes: b.durationMinutes || 20,
  language: b.language || 'en',
  tone: b.tone || 'friendly',
} : EMPTY);

const small = { margin: 0, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' };
const label = { fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--text-1)' };
const selectStyle = { height: 40, padding: '0 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--surface-1)', color: 'var(--text-1)', font: 'inherit', fontSize: 'var(--fs-body-md)' };
const cell = { padding: 6, verticalAlign: 'top', borderBottom: '1px solid var(--border)' };

/* The checker's words for one field, under it. Must-fix in red; advice muted. */
function Problems({ list }) {
  if (!list || !list.length) return null;
  return (
    <ul style={{ margin: '4px 0 0', paddingLeft: 18, display: 'grid', gap: 2 }}>
      {list.map((p, i) => (
        <li key={i} style={{ fontSize: 'var(--fs-caption)', color: p.severity === 'hard' ? 'var(--status-danger-text)' : 'var(--status-warning-text)' }}>
          {p.severity === 'hard' ? '' : 'Advice: '}{p.message}
        </li>
      ))}
    </ul>
  );
}

function Dropdown({ id, text, value, options, onChange, disabled }) {
  return (
    <label htmlFor={id} style={{ display: 'grid', gap: 6 }}>
      <span style={label}>{text}</span>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} style={selectStyle}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

/* The brief, read only: for the approver, and while it waits. */
function BriefView({ brief }) {
  const names = new Map((brief.rubric || []).map((c) => [c.id, c.name]));
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ margin: 0 }}><strong>Goal.</strong> {brief.goal}</p>
      <ol style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 4 }}>
        {(brief.questions || []).map((q) => <li key={q.itemId}>{q.text} <span style={{ color: 'var(--text-muted)' }}>— for: {names.get(q.criterionId) || 'not linked'}</span></li>)}
      </ol>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--fs-body-sm)' }}>
        <thead><tr>{['What is scored', 'Weight', 'Strong (5)', 'Adequate (3)', 'Weak (1)'].map((h) => <th key={h} style={{ ...cell, textAlign: 'left' }}>{h}</th>)}</tr></thead>
        <tbody>
          {(brief.rubric || []).map((c) => (
            <tr key={c.id}><td style={cell}>{c.name}</td><td style={cell}>{c.weight}</td><td style={cell}>{c.levels?.[5]}</td><td style={cell}>{c.levels?.[3]}</td><td style={cell}>{c.levels?.[1]}</td></tr>
          ))}
        </tbody>
      </table>
      <p style={small}>{brief.durationMinutes} minutes · {brief.language === 'hi' ? 'Hindi' : brief.language === 'mr' ? 'Marathi' : 'English'} · version {brief.version}</p>
    </div>
  );
}

export default function InterviewBriefPanel({ jobId, stage, onChanged }) {
  const base = `/interview-briefs/stage/${jobId}/${stage.stageId}`;
  const [state, setState] = useState(null);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [form, setForm] = useState(EMPTY);
  const [problems, setProblems] = useState([]);
  const [busy, setBusy] = useState('');
  const [library, setLibrary] = useState(null);
  const [pendingCanApprove, setPendingCanApprove] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(base);
      const d = r.data?.data || {};
      setState(d);
      // The editor starts from the working draft, else the version last sent
      // back or approved, else a blank brief.
      const newerSentBack = d.sentBack && (!d.approved || new Date(d.sentBack.decidedAt) > new Date(d.approved.decidedAt));
      const start = d.draft || (newerSentBack ? d.sentBack : d.approved) || null;
      setForm(contentOf(start));
      setProblems(start?.check?.problems || []);
      if (d.pending) {
        const one = await api.get(`/interview-briefs/${d.pending.briefId}`).catch(() => null);
        setPendingCanApprove(Boolean(one?.data?.canApprove));
      } else setPendingCanApprove(false);
    } catch (e) {
      setState({});
    }
  }, [base]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/interview-briefs/options').then((r) => { if (r.data?.data) setOptions(r.data.data); }).catch(() => {});
  }, []);

  const byField = useMemo(() => {
    const m = new Map();
    for (const p of problems) { if (!m.has(p.field)) m.set(p.field, []); m.get(p.field).push(p); }
    return m;
  }, [problems]);
  const under = (prefix) => problems.filter((p) => p.field === prefix || p.field.startsWith(`${prefix}.`));
  const mustFix = problems.filter((p) => p.severity === 'hard').length;
  const weightTotal = form.rubric.reduce((s, c) => s + (Number(c.weight) || 0), 0);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setQuestion = (i, patch) => set({ questions: form.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)) });
  const setLine = (i, patch) => set({ rubric: form.rubric.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const setLevel = (i, level, text) => setLine(i, { levels: { ...form.rubric[i].levels, [level]: text } });
  const addLine = () => {
    const next = form.rubric.reduce((m, c) => Math.max(m, Number(String(c.id).slice(1)) || 0), 0) + 1;
    set({ rubric: [...form.rubric, { id: `C${next}`, name: '', weight: 0, levels: { 5: '', 3: '', 1: '' } }] });
  };
  const removeLine = (i) => {
    const gone = form.rubric[i].id;
    set({ rubric: form.rubric.filter((_, j) => j !== i), questions: form.questions.map((q) => (q.criterionId === gone ? { ...q, criterionId: '' } : q)) });
  };

  const run = async (what, fn) => {
    setBusy(what);
    try { await fn(); } finally { setBusy(''); }
  };

  const save = async () => {
    const r = await api.put(`${base}/draft`, form);
    const b = r.data?.data;
    setProblems(b?.check?.problems || []);
    return b;
  };

  const saveDraft = () => run('save', async () => {
    try {
      const b = await save();
      toast.success(b?.check?.ok ? 'Draft saved. It is ready to send for approval.' : 'Draft saved. Fix the problems marked in red before sending it for approval.');
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the draft.'); }
  });

  const sendForApproval = () => run('send', async () => {
    try {
      const b = await save();
      if (!b?.check?.ok) { toast.error('Fix the problems marked in red first.'); return; }
      await api.post(`/interview-briefs/${b.briefId}/submit`);
      toast.success('Sent for approval. The stage waits until the approver decides.');
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      if (e.response?.data?.problems) setProblems(e.response.data.problems);
      toast.error(e.response?.data?.message || 'Could not send it for approval.');
    }
  });

  const draftWithAi = () => run('ai', async () => {
    try {
      const r = await api.post(`${base}/draft-with-ai`);
      setForm(contentOf(r.data?.data));
      setProblems(r.data?.data?.check?.problems || []);
      toast.success('The Assessment Designer has drafted the brief. Read it, change what you need, then send it for approval.');
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'The Assessment Designer could not draft it just now.'); }
  });

  const openLibrary = () => run('library', async () => {
    try {
      const r = await api.get(`/interview-briefs/library?excludeJobId=${jobId}`);
      setLibrary(r.data?.data || []);
    } catch (e) { toast.error('Could not load the other roles\' briefs.'); }
  });
  const copy = (fromBriefId) => run('copy', async () => {
    try {
      await api.post(`${base}/copy`, { fromBriefId });
      setLibrary(null);
      toast.success('Copied. Adjust it for this role, then send it for approval.');
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not copy that brief.'); }
  });

  const approve = (briefId) => run('approve', async () => {
    try {
      await api.post(`/interview-briefs/${briefId}/approve`, { note: reason });
      toast.success('Approved. The AI interview for this stage now follows this brief.');
      setReason('');
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not approve it.'); }
  });
  const sendBack = (briefId) => run('sendBack', async () => {
    try {
      await api.post(`/interview-briefs/${briefId}/send-back`, { reason });
      toast.success('Sent back, with your reason.');
      setReason('');
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not send it back.'); }
  });

  if (!state) return <Card padding="16px"><span style={{ color: 'var(--text-muted)' }}>Loading this stage's interview brief…</span></Card>;
  const { pending, approved, draft, sentBack } = state;
  const showSentBack = sentBack && !pending && (!approved || new Date(sentBack.decidedAt) > new Date(approved.decidedAt));
  const rubricOptions = [{ value: '', label: 'Choose…' }, ...form.rubric.map((c) => ({ value: c.id, label: c.name || `Line ${c.id.slice(1)}` }))];

  return (
    <section aria-label="This stage's interview brief" style={{ display: 'grid', gap: 12 }}>
      <Card padding="16px">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 600, margin: 0 }}>Interview brief</h3>
          {pending ? <StatusPill tone="warning">Waiting for approval</StatusPill>
            : approved ? <StatusPill tone="success">Ready</StatusPill>
              : draft ? <StatusPill tone="neutral">Draft</StatusPill>
                : <StatusPill tone="neutral">Not set</StatusPill>}
        </div>
        <p style={{ ...small, marginTop: 8 }}>
          Say what this interview should find out, what to ask, and what a good answer sounds like. The AI interviewer follows the brief once it is approved.
          {approved && ` Version ${approved.version} was approved by ${approved.decidedBy} on ${day(approved.decidedAt)}.`}
        </p>
        {showSentBack && (
          <p role="status" style={{ ...small, marginTop: 8, color: 'var(--status-danger-text)' }}>
            Sent back by {sentBack.decidedBy} on {day(sentBack.decidedAt)}: “{sentBack.decisionNote}”
          </p>
        )}
        {/* Feedback on the version in use (else the one waiting, else the
            draft): kept with the brief, nothing changes by itself. */}
        {(approved || pending || draft) && (
          <div style={{ marginTop: 8 }}>
            <SomethingWrong about="brief" briefId={(approved || pending || draft).briefId} />
          </div>
        )}
      </Card>

      {pending && (
        <Card padding="16px">
          <h4 style={{ fontSize: 'var(--fs-body-md)', fontWeight: 600, margin: '0 0 8px' }}>
            {pendingCanApprove ? 'Waiting for your approval' : 'Waiting for the approver'}
          </h4>
          <p style={{ ...small, marginBottom: 10 }}>
            Sent by {pending.submittedBy} on {day(pending.submittedAt)}. Until it is approved, this stage does not reach candidates.
          </p>
          <BriefView brief={pending} />
          <Problems list={(pending.check?.problems || []).filter((p) => p.severity === 'advice')} />
          {pendingCanApprove ? (
            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              <Input id={`brief-reason-${stage.stageId}`} label="Note (needed to send it back)" value={reason} onChange={(e) => setReason(e.target.value)} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button size="sm" onClick={() => approve(pending.briefId)} loading={busy === 'approve'}>Approve</Button>
                <Button size="sm" variant="secondary" disabled={!reason.trim()} onClick={() => sendBack(pending.briefId)} loading={busy === 'sendBack'}>Send back</Button>
              </div>
            </div>
          ) : (
            <p style={{ ...small, marginTop: 10 }}>The school's approver, someone who did not write or send this brief, approves it.</p>
          )}
        </Card>
      )}

      <Card padding="16px">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <Button size="sm" variant="secondary" onClick={draftWithAi} loading={busy === 'ai'} disabled={Boolean(busy)}>
            {busy === 'ai' ? 'Drafting — this takes a minute…' : 'Draft with AI'}
          </Button>
          <Button size="sm" variant="secondary" onClick={openLibrary} loading={busy === 'library'} disabled={Boolean(busy)}>Copy from another role</Button>
        </div>

        {library && (
          <div role="dialog" aria-label="Copy from another role" style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12, marginBottom: 12, display: 'grid', gap: 8 }}>
            {library.length ? library.map((l) => (
              <div key={l.briefId} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ flex: 1 }}>{l.role} — {l.stage} (version {l.version}, {l.questions} questions; scores {l.rubric.join(', ')})</span>
                <Button size="sm" onClick={() => copy(l.briefId)} disabled={Boolean(busy)}>Use this</Button>
              </div>
            )) : <p style={small}>No other role has an approved interview brief yet.</p>}
            <div><Button size="sm" variant="ghost" onClick={() => setLibrary(null)}>Close</Button></div>
          </div>
        )}

        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <Input id={`brief-goal-${stage.stageId}`} label="Goal: what should this interview find out?" multiline rows={3}
              value={form.goal} onChange={(e) => set({ goal: e.target.value })} />
            <Problems list={byField.get('goal')} />
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <span style={label}>What the interview scores</span>
            <p style={small}>For each line, say what a strong (5), adequate (3) and weak (1) answer sounds like. The weights must add up to 100.</p>
            <Problems list={byField.get('rubric')} />
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640 }}>
                <thead>
                  <tr>{['What is scored', 'Weight', 'Strong (5)', 'Adequate (3)', 'Weak (1)', ''].map((h) => <th key={h} style={{ ...cell, ...label, textAlign: 'left' }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {form.rubric.map((c, i) => (
                    <tr key={c.id}>
                      <td style={cell}><input aria-label={`Rubric line ${i + 1} name`} value={c.name} onChange={(e) => setLine(i, { name: e.target.value })} style={{ ...selectStyle, width: '100%' }} /></td>
                      <td style={{ ...cell, width: 80 }}><input aria-label={`Rubric line ${i + 1} weight`} type="number" min="1" max="100" value={c.weight} onChange={(e) => setLine(i, { weight: e.target.value === '' ? '' : Number(e.target.value) })} style={{ ...selectStyle, width: 70 }} /></td>
                      {[5, 3, 1].map((l) => (
                        <td key={l} style={cell}>
                          <textarea aria-label={`Rubric line ${i + 1} level ${l}`} rows={2} value={c.levels[l]} onChange={(e) => setLevel(i, l, e.target.value)} style={{ ...selectStyle, height: 'auto', width: '100%', padding: 6 }} />
                        </td>
                      ))}
                      <td style={cell}><Button size="sm" variant="ghost" onClick={() => removeLine(i)} disabled={form.rubric.length < 2}>Remove</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {form.rubric.map((c) => <Problems key={c.id} list={under(`rubric.${c.id}`)} />)}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Button size="sm" variant="ghost" onClick={addLine}>Add a rubric line</Button>
              <span style={{ ...small, color: weightTotal === 100 ? 'var(--text-2)' : 'var(--status-danger-text)' }}>Weights add up to {weightTotal} of 100.</span>
            </div>
            <Problems list={byField.get('rubric.weights')} />
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <span style={label}>Questions, in the order they are asked</span>
            <Problems list={byField.get('questions')} />
            {form.questions.map((q, i) => (
              <div key={i} style={{ display: 'grid', gap: 6, borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
                <Input id={`brief-q-${stage.stageId}-${i}`} label={`Question ${i + 1}`} value={q.text} onChange={(e) => setQuestion(i, { text: e.target.value })} />
                <div style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}>
                  <Dropdown id={`brief-q-${stage.stageId}-${i}-for`} text="Gathers evidence for" value={q.criterionId} options={rubricOptions} onChange={(v) => setQuestion(i, { criterionId: v })} />
                  <Button size="sm" variant="ghost" onClick={() => set({ questions: form.questions.filter((_, j) => j !== i) })} disabled={form.questions.length < 2}>Remove</Button>
                </div>
                <Problems list={byField.get(`questions.Q${String(i + 1).padStart(2, '0')}`)} />
              </div>
            ))}
            <div><Button size="sm" variant="ghost" onClick={() => set({ questions: [...form.questions, { text: '', criterionId: '' }] })}>Add a question</Button></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <div>
              <Dropdown id={`brief-length-${stage.stageId}`} text="Length" value={String(form.durationMinutes)}
                options={options.durations.map((d) => ({ value: String(d), label: `${d} minutes` }))} onChange={(v) => set({ durationMinutes: Number(v) })} />
              <Problems list={byField.get('durationMinutes')} />
            </div>
            <div>
              <Dropdown id={`brief-language-${stage.stageId}`} text="Language" value={form.language} options={options.languages} onChange={(v) => set({ language: v })} />
              <Problems list={byField.get('language')} />
            </div>
            <div>
              <Dropdown id={`brief-tone-${stage.stageId}`} text="Tone" value={form.tone} options={options.tones} onChange={(v) => set({ tone: v })} />
              <Problems list={byField.get('tone')} />
            </div>
          </div>

          {mustFix > 0 && (
            <p role="status" style={{ ...small, color: 'var(--status-danger-text)' }}>
              {mustFix === 1 ? 'One thing must be fixed' : `${mustFix} things must be fixed`} before this brief can go for approval.
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button size="sm" variant="secondary" onClick={saveDraft} loading={busy === 'save'} disabled={Boolean(busy)}>Save draft</Button>
            <Button size="sm" onClick={sendForApproval} loading={busy === 'send'} disabled={Boolean(busy)}>Send for approval</Button>
          </div>
        </div>
      </Card>
    </section>
  );
}
